import { Check, Copy, Link2, Send, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { can } from '@/features/auth/permissions'
import { useSession } from '@/features/auth/use-session'
import { RelativeTime } from '@/shared/components/common'
import { EmptyState, ErrorState, SkeletonRows } from '@/shared/components/data'
import { Button, Field, Input, Sheet, Switch } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'

import {
  useAceptarInvitacion,
  useEmitirInvitacion,
  useInvitaciones,
  useRevocarInvitacion,
} from '../api'
import { ESTADO_KEY, type Invitacion, type InvitacionEmitida } from '../types'

/**
 * Tab "Onboarding" de `/projects/:id`.
 *
 * El ciclo entero en una pantalla: mandar el brief, ver si lo respondieron,
 * leer lo que escribieron y aceptarlo.
 *
 * Aceptar es el acto que convierte lo que mandó alguien de afuera en cosas de
 * la empresa —un brief archivado y un contacto— y por eso lo hace una persona,
 * no el envío. Lo que llega por un link público se mira antes.
 */
export function ProjectOnboardingTab() {
  const { projectId = '' } = useParams()
  const t = useT()
  const { me } = useSession()
  const query = useInvitaciones(projectId)
  const [abriendo, setAbriendo] = useState(false)
  const [emitida, setEmitida] = useState<InvitacionEmitida | null>(null)

  const puedeEscribir = can(me, 'project:write')

  if (query.isPending) return <SkeletonRows rows={3} withSpine={false} />
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />
  }

  const invitaciones = query.data

  return (
    <div className="flex flex-col gap-3">
      {puedeEscribir ? (
        <div className="flex justify-end">
          <Button variant="primary" onClick={() => setAbriendo(true)}>
            <Send size={13} aria-hidden="true" />
            {t('onboarding.send')}
          </Button>
        </div>
      ) : null}

      {emitida ? <LinkReciente emitida={emitida} onClose={() => setEmitida(null)} /> : null}

      {invitaciones.length === 0 ? (
        <EmptyState
          title={t('onboarding.empty.title')}
          description={t('onboarding.empty.description')}
          icon={<Link2 size={18} aria-hidden="true" />}
          action={
            puedeEscribir ? (
              <Button variant="primary" onClick={() => setAbriendo(true)}>
                {t('onboarding.send')}
              </Button>
            ) : null
          }
        />
      ) : (
        <ul className="hairline flex flex-col overflow-hidden rounded-[var(--radius-card)] bg-surface-1">
          {invitaciones.map((invitacion) => (
            <FilaDeInvitacion
              key={invitacion.id}
              invitacion={invitacion}
              projectId={projectId}
              puedeEscribir={puedeEscribir}
            />
          ))}
        </ul>
      )}

      <FormularioDeEnvio
        projectId={projectId}
        open={abriendo}
        onClose={() => setAbriendo(false)}
        onEmitida={(resultado) => {
          setEmitida(resultado)
          setAbriendo(false)
        }}
      />
    </div>
  )
}

/**
 * EL LINK, UNA SOLA VEZ.
 *
 * En la base queda su SHA-256, así que ni el servidor puede volver a mostrarlo.
 * El cartel lo dice con todas las letras antes de que alguien lo cierre: si se
 * pierde, hay que emitir otro.
 */
function LinkReciente({ emitida, onClose }: { emitida: InvitacionEmitida; onClose: () => void }) {
  const t = useT()
  const [copiado, setCopiado] = useState(false)

  return (
    <div
      className="flex flex-col gap-2 rounded-[var(--radius-card)] border px-3 py-2.5"
      style={{ borderColor: 'hsl(var(--signal))', backgroundColor: 'hsl(var(--signal) / 0.06)' }}
    >
      <span className="text-xs text-text-1">
        {emitida.emailed
          ? t('onboarding.sentTo', { correo: emitida.invitation.sent_to_email })
          : t('onboarding.notSent')}
      </span>
      <span className="text-2xs text-text-2">{t('onboarding.linkOnce')}</span>
      <div className="flex flex-wrap items-center gap-2">
        <code className="num hairline min-w-0 flex-1 truncate rounded-[var(--radius-control)] bg-surface-1 px-2 py-1 text-2xs">
          {emitida.url}
        </code>
        <Button
          onClick={() => {
            void navigator.clipboard.writeText(emitida.url).then(
              () => {
                setCopiado(true)
                toast.success(t('onboarding.copied'))
              },
              () => toast.error(t('onboarding.copyFailed')),
            )
          }}
        >
          {copiado ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
          {t('common.action.copy')}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          {t('common.action.close')}
        </Button>
      </div>
    </div>
  )
}

function FilaDeInvitacion({
  invitacion,
  projectId,
  puedeEscribir,
}: {
  invitacion: Invitacion
  projectId: string
  puedeEscribir: boolean
}) {
  const t = useT()
  const aceptar = useAceptarInvitacion(projectId)
  const revocar = useRevocarInvitacion(projectId)
  const [crearContacto, setCrearContacto] = useState(true)
  const [viendo, setViendo] = useState(false)

  return (
    <li className="hairline-b flex flex-col gap-2 px-3 py-2.5 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-text-1">{invitacion.sent_to_email}</span>
        <span className="hairline rounded-[var(--radius-control)] px-1.5 py-0.5 text-2xs text-text-2">
          {t(ESTADO_KEY[invitacion.status])}
        </span>
        {invitacion.submitted_at ? (
          <span className="text-2xs text-text-3">
            {t('onboarding.submittedAt')} <RelativeTime value={invitacion.submitted_at} />
          </span>
        ) : (
          <span className="text-2xs text-text-3">
            {t('onboarding.expiresAt')} <RelativeTime value={invitacion.expires_at} />
          </span>
        )}

        <span className="ml-auto flex items-center gap-2">
          {invitacion.status === 'submitted' ? (
            <>
              <Button onClick={() => setViendo((v) => !v)}>
                {viendo ? t('onboarding.hideAnswers') : t('onboarding.viewAnswers')}
              </Button>
              {puedeEscribir ? (
                <>
                  <Switch
                    label={t('onboarding.alsoCreateContact')}
                    checked={crearContacto}
                    onChange={setCrearContacto}
                  />
                  <Button
                    variant="primary"
                    disabled={aceptar.isPending}
                    onClick={() =>
                      aceptar.mutate({ id: invitacion.id, createContact: crearContacto })
                    }
                  >
                    {t('onboarding.accept')}
                  </Button>
                </>
              ) : null}
            </>
          ) : null}

          {invitacion.document_id ? (
            <Link
              to={`/projects/${projectId}/documentos/${invitacion.document_id}`}
              className="text-2xs text-signal underline-offset-2 hover:underline"
            >
              {t('onboarding.openBrief')}
            </Link>
          ) : null}

          {puedeEscribir && invitacion.status === 'pending' ? (
            <Button
              variant="danger"
              disabled={revocar.isPending}
              onClick={() => revocar.mutate(invitacion.id)}
              title={t('onboarding.revoke')}
              aria-label={t('onboarding.revoke')}
            >
              <Trash2 size={13} aria-hidden="true" />
            </Button>
          ) : null}
        </span>
      </div>

      {viendo && invitacion.answers ? <Respuestas invitacion={invitacion} /> : null}
    </li>
  )
}

/** Lo que mandó el cliente, tal cual, para poder leerlo antes de aceptarlo. */
function Respuestas({ invitacion }: { invitacion: Invitacion }) {
  const t = useT()
  const answers = invitacion.answers ?? {}

  return (
    <div className="hairline flex flex-col gap-2 rounded-[var(--radius-card)] bg-surface-2 px-3 py-2.5">
      <span className="text-2xs text-text-3">
        {t('onboarding.answeredBy', {
          nombre: invitacion.contact_name ?? '—',
          correo: invitacion.contact_email ?? '—',
        })}
      </span>
      <dl className="flex flex-col gap-2">
        {Object.entries(answers).map(([id, valor]) => (
          <div key={id} className="flex flex-col gap-0.5">
            <dt className="num text-2xs text-text-3">{id}</dt>
            <dd className="text-xs whitespace-pre-wrap text-text-1">
              {Array.isArray(valor)
                ? valor
                    .filter((fila) => fila.some((celda) => celda.trim()))
                    .map((fila) => fila.join(' · '))
                    .join('\n')
                : valor}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function FormularioDeEnvio({
  projectId,
  open,
  onClose,
  onEmitida,
}: {
  projectId: string
  open: boolean
  onClose: () => void
  onEmitida: (resultado: InvitacionEmitida) => void
}) {
  const t = useT()
  const emitir = useEmitirInvitacion(projectId)
  const [email, setEmail] = useState('')
  const [nombre, setNombre] = useState('')
  const [dias, setDias] = useState(14)

  if (!open) return null

  const valido = email.includes('@') && dias >= 1 && dias <= 90

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={<h2 className="text-base text-text-1">{t('onboarding.send')}</h2>}
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('common.action.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!valido || emitir.isPending}
            onClick={() =>
              emitir.mutate(
                {
                  sent_to_email: email.trim(),
                  sent_to_name: nombre.trim() || null,
                  expires_in_days: dias,
                },
                { onSuccess: onEmitida },
              )
            }
          >
            {emitir.isPending ? t('onboarding.sending') : t('onboarding.generate')}
          </Button>
        </div>
      }
    >
      <form className="flex flex-col gap-3" onSubmit={(e) => e.preventDefault()}>
        <p className="text-xs text-text-2">{t('onboarding.sendHint')}</p>
        <Field label={t('common.label.email')} htmlFor="ob-email">
          <Input
            id="ob-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label={t('common.label.name')} htmlFor="ob-name">
          <Input id="ob-name" value={nombre} onChange={(e) => setNombre(e.target.value)} />
        </Field>
        <Field
          label={t('onboarding.expiresInDays')}
          htmlFor="ob-days"
          hint={t('onboarding.expiresInDaysHint')}
        >
          <Input
            id="ob-days"
            type="number"
            min={1}
            max={90}
            value={dias}
            onChange={(e) => setDias(Number(e.target.value))}
          />
        </Field>
      </form>
    </Sheet>
  )
}
